import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Chip } from '@mui/material';
import { deleteCity, getCities } from '../../../../../api/endpoints/cmsContent';
import type { City } from '../../../../../api/types/cmsContent';
import { ConfirmDialog } from '../../../../../components/ConfirmDialog';
import {
  ListPageScaffold,
  RowActions,
  useDeleteResource,
  type DataTableColumn,
} from '../../../../../components/common';
import { CityFormDialog } from '../CityFormDialog';

/** `SiteDetailPage` içinde bir sekme olarak render edilir; `siteId` sabit prop. */
export default function CityListPage({ siteId }: { siteId: number }) {
  const [search, setSearch] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState<City | null>(null);

  const query = useQuery({ queryKey: ['cms', 'content', 'cities', siteId], queryFn: () => getCities(siteId) });
  const rows: City[] = useMemo(() => query.data?.cities ?? [], [query.data]);

  const filteredRows = useMemo(() => {
    const term = search.trim().toLocaleLowerCase('tr');
    if (!term) return rows;
    return rows.filter((row) =>
      [row.name, row.displayName, row.keywords, row.description]
        .filter((v) => v !== undefined && v !== null && v !== '')
        .join(' ')
        .toLocaleLowerCase('tr')
        .includes(term)
    );
  }, [rows, search]);

  const del = useDeleteResource<number>({
    mutationFn: (id) => deleteCity(id),
    invalidateKeys: [['cms', 'content', 'cities']],
    successMessage: 'Şehir silindi.',
  });
  const deletingName = rows.find((r) => r.id === del.target)?.name ?? '';

  const columns: DataTableColumn<City>[] = [
    { id: 'name', header: 'Ad', cell: (r) => r.name ?? '—' },
    { id: 'displayName', header: 'Görünen Ad', cell: (r) => r.displayName ?? '—' },
    { id: 'type', header: 'Tip', cell: (r) => (r.type ? <Chip size="small" label={r.type} /> : '—') },
  ];

  return (
    <ListPageScaffold
      title="Şehirler"
      headerVariant="section"
      createLabel="Yeni Şehir"
      onCreate={() => setCreateOpen(true)}
      search={{ value: search, onChange: setSearch, placeholder: 'Ad, açıklama, anahtar kelime...' }}
      query={query}
      rows={filteredRows}
      columns={columns}
      getRowKey={(r) => r.id}
      emptyTitle={rows.length === 0 ? 'Kayıtlı şehir bulunamadı.' : 'Aramayla eşleşen şehir bulunamadı.'}
      rowActions={(r) => <RowActions onEdit={() => setEditing(r)} onDelete={() => del.request(r.id)} />}
    >
      <CityFormDialog open={createOpen} city={null} siteId={siteId} onClose={() => setCreateOpen(false)} />
      <CityFormDialog open={!!editing} city={editing} siteId={siteId} onClose={() => setEditing(null)} />
      <ConfirmDialog
        {...del.confirmDialogProps}
        title="Şehri sil"
        description={`"${deletingName}" şehrini silmek istediğinize emin misiniz?`}
      />
    </ListPageScaffold>
  );
}
